import { addLocalePrefix } from 'hooks/router/usePush';
import { setAuthCookie, setTrustedDeviceCookie } from 'utils/cookies/setCookies';
import { Notification } from 'state/middleware/notificationMiddleware';
import isAdmin from 'utils/auth/isAdmin';
import { logout, refresh } from './actions';
import types from './types';
import { add } from '../notifications';

const {
  AUTH_CALLBACK, AUTH_INIT, SET_USER, LOGIN_SUCCESS, LOGOUT_SUCCESS, REFRESH_SUCCESS, RESEND_SUCCESS, VERIFY_SUCCESS,
  PASSKEY_LOGIN_SUCCESS,
} = types;
/*
  This middleware monitors the verify 2FA endpoint and set trusted device to cookies whenever trusted device info is returned.
*/
let timer = null;

const REDIRECT_LINK = '/financial/currentStatus';

const getRedirectUrl = search => {
  const nextURL = new URLSearchParams(search).get('nextURL');
  if (!nextURL) {
    return REDIRECT_LINK;
  }

  try {
    // decodeURIComponent throws URIError on malformed percent-escapes (e.g. a lone '%'). Fall
    // back to the raw value so a bad nextURL can't abort the whole post-login redirect.
    return decodeURIComponent(nextURL);
  }
  catch {
    return nextURL;
  }
};

export default history => ({ dispatch, getState }) => next => action => {
  const { result, type } = action;

  const dispatchRefresh = () => {
    // Use the Session Token TTL to set the timeout to call the refresh token action
    // The TTL is in seconds, convert it to milliseconds and reduce 30 seconds to avoid timing issues
    const idleTimeOut = (result.ttl - 30) * 1000;
    timer = setTimeout(() => dispatch(refresh()), idleTimeOut);
  };

  switch (type) {
    case AUTH_CALLBACK:
    case LOGIN_SUCCESS: {
      next(action); // trigger the next action to update the state first
      // A 2FA challenge always wins over a session-start. Gateway responses are mutually
      // exclusive today, but checking 2FA first stops the frontend from silently skipping
      // the 2FA step if an upstream payload ever carries both used2FA and a challenge.
      if (result.twoFactorRequestId || result.auth2faOptions?.length > 0) {
        history.push(addLocalePrefix('/verify2faCode'));
        return;
      }
      if ('used2FA' in result && !result.used2FA) {
        setAuthCookie(result);
        history.push(addLocalePrefix(REDIRECT_LINK));
        return;
      }
      // Neither a 2FA challenge nor a fresh session — treat as a broken login response.
      dispatch(add(new Notification('notifications.title.error', 'notifications.2fa.noRecipientOptions.error', type, 'error', () => dispatch(logout()))));
      return;
    }
    case AUTH_INIT: {
      next(action);
      // Redirect the whole browser to the authentication service (e.g. Google SSO).
      // Guard against a missing location so a malformed response doesn't navigate to "undefined".
      if (result?.location) {
        window.location.replace(result.location);
      }
      return;
    }
    case REFRESH_SUCCESS: {
      if (!result?.sendTwoFactorCode) {
        // The session already started if no 2FA request ID was sent
        setAuthCookie(result);
        dispatchRefresh();
      }
      break;
    }
    case LOGOUT_SUCCESS: {
      // Clear the timer if the user is logged out
      clearTimeout(timer);
      break;
    }
    // A verified passkey assertion starts a session directly (it already
    // satisfies user verification), so it follows the exact VERIFY_SUCCESS
    // path: set cookies, schedule refresh, redirect by role.
    case PASSKEY_LOGIN_SUCCESS:
    case VERIFY_SUCCESS: {
      next(action); // trigger the next action to update the state first

      const { user, trustedDevice } = result;

      if (trustedDevice) {
        setTrustedDeviceCookie(trustedDevice);
      }

      setAuthCookie(result);
      dispatchRefresh();
      const isUserAdmin = isAdmin(user);
      const { auth } = getState();

      if (auth.admin || isUserAdmin) {
        history.push(addLocalePrefix('/selectOwnerId'));
      }
      else {
        const nextURL = getRedirectUrl(history.location.search);
        history.push(addLocalePrefix(nextURL));
      }
      // next(action) already fired at the top of this case — return to avoid the
      // trailing next(action) at the end of the middleware (double-dispatch).
      return;
    }
    case SET_USER: {
      const nextURL = getRedirectUrl(history.location.search);
      history.push(addLocalePrefix(nextURL));
      break;
    }
    case RESEND_SUCCESS: {
      if (result.twoFactorRequestGenerated) {
        dispatch(add(new Notification(null, 'notifications.2fa.resendCode.successful', type)));
      }
      break;
    }
    default: {
      next(action);
      return;
    }
  }
  next(action);
};
